import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AffiliateMenuManager.css';

const AffiliateMenuManager = () => {
  const [affiliates, setAffiliates] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAffiliate, setSelectedAffiliate] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('PENDING');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [selectedStatus]);

  const handleSelectAll = (checked) => {
    if (checked) {
      // Select all filtered items
      const newSelected = new Set(filteredMenuItems.map(item => item._id));
      setSelectedItems(newSelected);
    } else {
      // Clear selection
      setSelectedItems(new Set());
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [affiliatesResponse, menuItemsResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/admin/affiliates`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        // Only fetch affiliate menus (not personal user menus)
        fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items?status=${selectedStatus}&affiliateOnly=true`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      if (!affiliatesResponse.ok || !menuItemsResponse.ok) {
        const errorData = await (affiliatesResponse.ok ? menuItemsResponse : affiliatesResponse).json();
        throw new Error(errorData.error || 'Failed to fetch data');
      }

      const [affiliatesData, menuItemsData] = await Promise.all([
        affiliatesResponse.json(),
        menuItemsResponse.json()
      ]);

      setAffiliates(affiliatesData);
      setMenuItems(menuItemsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleModerate = async (itemId, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${itemId}/moderate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error('Failed to update moderation status');
      }

      // Refresh the data after moderation
      fetchData();
      // Clear the selection
      setSelectedItems(new Set());
    } catch (err) {
      console.error('Error moderating item:', err);
      setError(err.message);
    }
  };

  const handleBulkApprove = async () => {
    try {
      const token = localStorage.getItem('token');
      const promises = Array.from(selectedItems).map(itemId =>
        fetch(`${process.env.REACT_APP_API_URL}/admin/menu-items/${itemId}/moderate`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ status: 'APPROVED' })
        })
      );

      await Promise.all(promises);
      fetchData();
      setSelectedItems(new Set());
    } catch (err) {
      console.error('Error in bulk approval:', err);
      setError(err.message);
    }
  };

  const viewDetails = (menuId) => {
    if (menuId) {
      navigate(`/menu/${menuId}?context=admin`);
    } else {
      setError('Menu ID not found');
    }
  };

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const filteredMenuItems = menuItems.filter(item => 
    selectedAffiliate === 'all' || item.affiliateId === selectedAffiliate
  );

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return (
      <div className="error">
        Error: {error}
        <button 
          onClick={() => {
            setError(null);
            fetchData();
          }}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="affiliate-menu-manager">
      <div className="filters-section">
        <div className="filter-group">
          <label>Filter by Affiliate:</label>
          <select 
            value={selectedAffiliate} 
            onChange={(e) => setSelectedAffiliate(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Affiliates</option>
            {affiliates.map(affiliate => (
              <option key={affiliate._id} value={affiliate._id}>
                {affiliate.businessName || affiliate.userEmail || 'Unknown Affiliate'}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Filter by Status:</label>
          <select 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="filter-select"
          >
            <option value="PENDING">Pending</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
          </select>
        </div>

        <div className="filter-group">
          <label>
            <input
              type="checkbox"
              checked={selectedItems.size === filteredMenuItems.length && filteredMenuItems.length > 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
              className="select-all-checkbox"
            />
            Select All ({filteredMenuItems.length} items)
          </label>
        </div>
      </div>

      {selectedItems.size > 0 && (
        <div className="bulk-actions">
          <button 
            className="bulk-approve-button"
            onClick={handleBulkApprove}
          >
            Approve Selected ({selectedItems.size})
          </button>
        </div>
      )}

      <div className="menu-items-section">
        <h2>{filteredMenuItems.length} Menu Items</h2>
        <div className="menu-items-grid">
          {filteredMenuItems.map(item => (
            <div key={item._id} className="menu-item-card">
              <div className="item-select">
                <input
                  type="checkbox"
                  checked={selectedItems.has(item._id)}
                  onChange={() => toggleItemSelection(item._id)}
                />
              </div>
              <img 
                src={item.image} 
                alt={item.title} 
                className="item-image"
                onClick={() => viewDetails(item.menuId)}
              />
              <div className="item-details">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <p className="cuisine">Cuisine: {item.cuisine}</p>
                <p className="affiliate">
                  Affiliate: {item.affiliateBusinessName || item.affiliateEmail || 'Unknown'}
                </p>
                <div className="moderation-actions">
                  <button 
                    className="approve-button"
                    onClick={() => handleModerate(item._id, 'APPROVED')}
                  >
                    Approve
                  </button>
                  <button 
                    className="reject-button"
                    onClick={() => handleModerate(item._id, 'REJECTED')}
                  >
                    Reject
                  </button>
                  <button 
                    className="view-details-button"
                    onClick={() => viewDetails(item.menuId)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AffiliateMenuManager; 